import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import {
  ForwardedRef,
  forwardRef,
  Fragment,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';

import { getColumnCanGlobalFilter, globalFilterFn } from '../DataTable.types';

import { CustomDataTableProps, getRowValues } from './CustomDataTable.types';

export const CustomDataTableInner = <TData, TColumns extends ColumnDef<TData, unknown>[]>(
  {
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    onPaginationChange,
    empty,
    enableRowSelection = false,
    enableExpanding = false,
    enablePagination = false,
    disableMultiExpansion,
    getSubRows,
    state,
    children,
    ...options
  }: CustomDataTableProps<TData, TColumns>,
  ref: ForwardedRef<Table<TData>>
) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: options.initialState?.pagination?.pageIndex ?? 0,
    pageSize: options.initialState?.pagination?.pageSize ?? 20,
  });

  const onExpandedChange = useCallback(
    (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
      const oldValue = expanded;
      const newValue = typeof updater === 'function' ? updater(oldValue) : updater;
      if (disableMultiExpansion && Object.keys(newValue).length > 1) {
        for (const key of Object.keys(oldValue)) {
          if (Object.keys(newValue).includes(key)) delete (newValue as Record<string, true>)[key];
        }
      }
      setExpanded(newValue);
    },
    [setExpanded, expanded, disableMultiExpansion]
  );

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: sorting => {
      setSorting(sorting);
      onSortingChange?.(sorting);
    },
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: columnFilters => {
      setColumnFilters(columnFilters);
      onColumnFiltersChange?.(columnFilters);
    },
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSubRows: getSubRows,
    onExpandedChange,
    getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
    onColumnVisibilityChange: columnVisibility => {
      setColumnVisibility(columnVisibility);
      onColumnVisibilityChange?.(columnVisibility);
    },
    enableRowSelection,
    onRowSelectionChange: rowSelection => {
      setRowSelection(rowSelection);
      onRowSelectionChange?.(rowSelection);
    },
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onPaginationChange: pagination => {
      if (!enablePagination) return;
      setPagination(pagination);
      onPaginationChange?.(pagination);
    },
    filterFns: {
      arrIncludes: (row, columnId, filterValue?: (string | number)[]) =>
        filterValue?.includes(row.getValue(columnId)) ?? true,
    },
    globalFilterFn,
    getColumnCanGlobalFilter,
    state: {
      expanded,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(enablePagination ? { pagination } : undefined),
      ...state,
    },
    ...options,
  });

  useImperativeHandle(ref, () => table, [table]);

  return table.getRowModel().rows?.length ? (
    <>
      {table.getRowModel().rows.map(row => {
        return <Fragment key={row.id}>{children(getRowValues(row), row)}</Fragment>;
      })}
    </>
  ) : (
    <>{empty}</>
  );
};

export const CustomDataTable = forwardRef(CustomDataTableInner) as <
  TData,
  TColumns extends ColumnDef<TData, unknown>[],
>(
  props: CustomDataTableProps<TData, TColumns> & { ref?: Ref<Table<TData>> }
) => ReturnType<typeof CustomDataTableInner>;
